import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import {
  getSchedulerSummary, getSchedulerRuns, triggerSchedulerJob, dryRunSchedulerJob,
  getSchedulerConfig, putSchedulerJobConfig,
  getSchedulerAuditLog,
} from '../lib/api';
import {
  Clock, CheckCircle, XCircle, Lightning, ArrowsClockwise, WarningCircle,
  Pause, Play, FloppyDisk, Eye, X, ClockCounterClockwise,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

// Human-readable labels + one-liner descriptions for each registered job.
// Kept in-sync with /app/backend/schedulers.py → JOB_FNS.
const JOB_META = {
  cycle_count:        { label: 'Cycle Count Scheduler',        desc: 'Auto-generates weekly cycle-count tasks on the day/hour you configure.', cadence: 'Weekly · configurable' },
  low_stock:          { label: 'Low-Stock Scanner',            desc: 'Walks every SKU and fires low-stock / stockout alerts to your notify list.', cadence: 'Hourly' },
  writeoff:           { label: 'Write-Off Threshold Scanner',  desc: 'Alerts when aggregated write-offs exceed your $ threshold in the lookback window.', cadence: 'Hourly' },
  broadcasts:         { label: 'Scheduled Broadcasts',         desc: 'Delivers admin-scheduled announcements (Admin → Broadcasts) at their due time.', cadence: 'Every 60s' },
  leaderboard:        { label: 'Cycle-Count Leaderboard',      desc: 'Publishes weekly top-performers digest to your notify list every Monday 09:00 UTC.', cadence: 'Weekly · Mon 09:00 UTC' },
  supplier_scorecard: { label: 'Supplier Scorecard Digest',    desc: 'Publishes weekly 90-day supplier reliability digest every Tuesday 10:00 UTC.', cadence: 'Weekly · Tue 10:00 UTC' },
  inbound_ops_digest: { label: 'Inbound Ops Weekly Digest',    desc: 'Bundles supplier + labour + write-off KPIs into one exec digest Mondays 08:00 UTC.', cadence: 'Weekly · Mon 08:00 UTC' },
  retention:          { label: 'Retention Job',                desc: 'Archives + purges data per your retention policies.', cadence: 'Daily' },
  replenishment:      { label: 'Replenishment Safety-Net',     desc: 'Re-scans fixed locations for below-min stock not caught by event triggers.', cadence: 'Every 5min' },
};

const JOB_ORDER = [
  'low_stock', 'writeoff', 'cycle_count', 'replenishment', 'broadcasts',
  'leaderboard', 'supplier_scorecard', 'inbound_ops_digest', 'retention',
];

function _fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function _fmtRelative(iso) {
  if (!iso) return 'never';
  const delta = (Date.now() - new Date(iso).getTime()) / 1000;
  if (delta < 60) return `${Math.round(delta)}s ago`;
  if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.round(delta / 3600)}h ago`;
  return `${Math.round(delta / 86400)}d ago`;
}

function _fmtResult(result) {
  if (!result) return '';
  if (typeof result !== 'object') return String(result);
  if (result.skipped) return `skipped: ${result.skipped}`;
  const bits = [];
  if ('alerts_created' in result)    bits.push(`${result.alerts_created} alerts`);
  if ('stockouts_created' in result) bits.push(`${result.stockouts_created} stockouts`);
  if ('fired' in result)             bits.push(`${result.fired} fired`);
  if ('created' in result)           bits.push(`${result.created} created`);
  if ('delivered' in result)         bits.push(`${result.delivered} delivered`);
  if ('checked' in result)           bits.push(`${result.checked} checked`);
  if ('published' in result)         bits.push(`${result.published} published`);
  if ('suppliers_published' in result) bits.push(`${result.suppliers_published} suppliers`);
  if ('total_archived' in result)    bits.push(`${result.total_archived} archived`);
  return bits.join(' · ') || 'ok';
}


const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];


function JobCard({ job, summary, config, onTrigger, onDryRun, onSaveConfig, busy }) {
  const meta = JOB_META[job] || { label: job, desc: '', cadence: '' };
  const last = summary?.last_run;
  const ok = last?.ok !== false;

  // Local draft state — saves as one PUT on click, revert on cancel
  const [draft, setDraft] = useState(null);
  const editing = draft !== null;
  const enabled = (draft ?? config)?.enabled ?? true;
  const dow = (draft ?? config)?.day_of_week ?? 0;
  const hour = (draft ?? config)?.hour_utc ?? 0;
  const schedulable = !!config?.schedulable;

  const dirty =
    draft !== null && (
      draft.enabled !== config.enabled ||
      (schedulable && draft.day_of_week !== config.day_of_week) ||
      (schedulable && draft.hour_utc !== config.hour_utc)
    );

  const _startEdit = (patch = {}) => {
    // Merge with EXISTING draft (if any) so sequential edits on day + hour
    // don't clobber each other. Using the functional setter is essential —
    // reading the outer `draft` directly would race with React 18 batching.
    setDraft((prev) => ({
      enabled: config.enabled,
      day_of_week: config.day_of_week,
      hour_utc: config.hour_utc,
      ...(prev || {}),
      ...patch,
    }));
  };

  const _togglePause = async () => {
    // Inline toggle — save immediately (no "apply" button for just the switch)
    const patch = { enabled: !enabled };
    try {
      await onSaveConfig(job, patch);
      toast.success(patch.enabled ? `${meta.label} resumed` : `${meta.label} paused`);
    } catch (e) {
      toast.error(`Update failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const _saveSchedule = async () => {
    try {
      await onSaveConfig(job, {
        day_of_week: draft.day_of_week,
        hour_utc: draft.hour_utc,
      });
      setDraft(null);
      toast.success(`${meta.label} schedule updated`);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <Card
      className={`p-5 flex flex-col gap-3 ${!enabled ? 'opacity-60' : ''}`}
      data-testid={`scheduler-job-${job}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-bold text-[15px] tracking-tight truncate">{meta.label}</div>
            {!enabled && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/15 text-amber-600 rounded font-mono">
                paused
              </span>
            )}
          </div>
          <div className="text-xs opacity-70 mt-0.5">
            {schedulable && enabled
              ? `Weekly · ${DAYS[config.day_of_week ?? 0]} ${String(config.hour_utc ?? 0).padStart(2, '0')}:00 UTC`
              : meta.cadence}
          </div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            title="Preview (dry-run) — no side effects"
            data-testid={`scheduler-preview-${job}`}
            disabled={busy}
            onClick={() => onDryRun(job)}
          >
            <Eye className="size-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            title={enabled ? 'Pause job' : 'Resume job'}
            data-testid={`scheduler-toggle-${job}`}
            disabled={busy}
            onClick={_togglePause}
          >
            {enabled ? <Pause className="size-4" /> : <Play className="size-4" weight="fill" />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-3 text-[11px] tracking-wide uppercase"
            data-testid={`scheduler-trigger-${job}`}
            disabled={busy || !enabled}
            onClick={() => onTrigger(job)}
          >
            {busy ? <ArrowsClockwise className="size-4 animate-spin" /> : <Lightning className="size-4" />}
            <span className="ml-1.5">Run now</span>
          </Button>
        </div>
      </div>

      <div className="text-[13px] opacity-80 leading-snug">{meta.desc}</div>

      {schedulable && (
        <div className="flex items-center gap-2 text-[12px] pt-1">
          <span className="opacity-60 uppercase tracking-wider text-[10px] font-mono">Schedule</span>
          <select
            value={dow}
            onChange={(e) => _startEdit({ day_of_week: parseInt(e.target.value, 10) })}
            disabled={!enabled}
            data-testid={`scheduler-day-${job}`}
            className="h-8 px-2 text-[12px] font-mono bg-background border border-border rounded"
          >
            {DAYS.map((d, i) => (
              <option key={i} value={i} label={d}></option>
            ))}
          </select>
          <select
            value={hour}
            onChange={(e) => _startEdit({ hour_utc: parseInt(e.target.value, 10) })}
            disabled={!enabled}
            data-testid={`scheduler-hour-${job}`}
            className="h-8 px-2 text-[12px] font-mono bg-background border border-border rounded"
          >
            {Array.from({ length: 24 }).map((_, i) => (
              <option key={i} value={i} label={`${String(i).padStart(2, '0')}:00`}></option>
            ))}
          </select>
          <span className="opacity-60 text-[11px]">UTC</span>
          {editing && dirty && (
            <>
              <Button
                size="sm"
                variant="default"
                className="h-8 px-2 ml-auto"
                data-testid={`scheduler-save-${job}`}
                onClick={_saveSchedule}
              >
                <FloppyDisk className="size-3.5" />
                <span className="ml-1 text-[11px]">Save</span>
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2"
                onClick={() => setDraft(null)}
              >
                <span className="text-[11px]">Cancel</span>
              </Button>
            </>
          )}
        </div>
      )}

      <div className="border-t border-border/60 pt-3 flex items-center gap-2 text-[12px]">
        {last ? (
          <>
            {ok ? (
              <CheckCircle className="size-4 text-emerald-500" weight="fill" />
            ) : (
              <XCircle className="size-4 text-red-500" weight="fill" />
            )}
            <span className="font-mono">{_fmtRelative(last.ran_at)}</span>
            <span className="opacity-60">·</span>
            <span className="font-mono opacity-70">{_fmtDuration(last.duration_ms)}</span>
            <span className="opacity-60">·</span>
            <span className="opacity-80 truncate" data-testid={`scheduler-last-result-${job}`}>
              {ok ? _fmtResult(last.result) : (last.error || 'error')}
            </span>
          </>
        ) : (
          <>
            <Clock className="size-4 opacity-50" />
            <span className="opacity-60">Not yet run for your tenant</span>
          </>
        )}
      </div>
    </Card>
  );
}


export default function SchedulerSettings() {
  const [summary, setSummary] = useState({ jobs: [] });
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState({ jobs: [] });
  const [auditEntries, setAuditEntries] = useState([]);
  const [busyJob, setBusyJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const summaryByJob = useMemo(() => {
    const m = {};
    for (const j of (summary.jobs || [])) m[j.job] = j;
    return m;
  }, [summary]);

  const configByJob = useMemo(() => {
    const m = {};
    for (const j of (config.jobs || [])) m[j.job] = j;
    return m;
  }, [config]);

  const load = async () => {
    try {
      const [s, r, c, a] = await Promise.all([
        getSchedulerSummary(),
        getSchedulerRuns({ limit: 30 }),
        getSchedulerConfig(),
        getSchedulerAuditLog({ limit: 50 }),
      ]);
      setSummary(s.data);
      setRows(r.data.items || []);
      setConfig(c.data);
      setAuditEntries(a.data.entries || []);
    } catch (e) {
      toast.error('Failed to load scheduler runs');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleTrigger = async (job) => {
    setBusyJob(job);
    try {
      const { data } = await triggerSchedulerJob(job);
      if (data.ok) {
        const r = data.result || {};
        toast.success(r.skipped
          ? `${JOB_META[job]?.label || job}: skipped (${r.skipped})`
          : `${JOB_META[job]?.label || job}: ${_fmtResult(r) || 'ok'}`);
      } else {
        toast.error(`${job} failed: ${data.error || 'unknown error'}`);
      }
      await load();
    } catch (e) {
      toast.error(`Trigger failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyJob(null);
    }
  };

  const handleSaveConfig = async (job, patch) => {
    await putSchedulerJobConfig(job, patch);
    // Refresh config (cheap — small endpoint), not full page reload
    const c = await getSchedulerConfig();
    setConfig(c.data);
  };

  const [preview, setPreview] = useState(null);

  const handleDryRun = async (job) => {
    setBusyJob(job);
    try {
      const { data } = await dryRunSchedulerJob(job);
      setPreview({ job, ...data });
      if (!data.ok) {
        toast.error(`${JOB_META[job]?.label || job} preview failed: ${data.error}`);
      }
    } catch (e) {
      toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyJob(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8" data-testid="scheduler-settings-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Scheduler Settings</h1>
            <p className="mt-2 text-sm opacity-70 max-w-2xl">
              Every background job runs <span className="font-semibold">scoped to your company</span>.
              Your schedule, your notify list, your data — nothing touches other tenants.
              Use <span className="font-mono text-xs">Run now</span> to fire a job manually for your
              tenant without waiting for the next tick.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="scheduler-refresh-btn">
            <ArrowsClockwise className="size-4" />
            <span className="ml-2">Refresh</span>
          </Button>
        </div>

        {loading ? (
          <div className="text-sm opacity-60">Loading scheduler state…</div>
        ) : (
          <>
            <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {JOB_ORDER.map((j) => (
                <JobCard
                  key={j}
                  job={j}
                  summary={summaryByJob[j]}
                  config={configByJob[j]}
                  onTrigger={handleTrigger}
                  onDryRun={handleDryRun}
                  onSaveConfig={handleSaveConfig}
                  busy={busyJob === j}
                />
              ))}
            </section>

            <section className="space-y-3">
              <h2 className="text-lg font-bold tracking-tight">Recent runs</h2>
              <Card className="overflow-hidden">
                <table className="w-full text-[13px]" data-testid="scheduler-runs-table">
                  <thead className="bg-muted/40 text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2.5">Job</th>
                      <th className="text-left px-4 py-2.5">Ran</th>
                      <th className="text-left px-4 py-2.5">Duration</th>
                      <th className="text-left px-4 py-2.5">Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={4} className="px-4 py-6 text-center opacity-60">No runs yet for your tenant.</td></tr>
                    ) : rows.map((r, i) => (
                      <tr key={i} className="border-t border-border/40">
                        <td className="px-4 py-2.5 font-mono">{r.job}</td>
                        <td className="px-4 py-2.5 font-mono">{_fmtRelative(r.ran_at)}</td>
                        <td className="px-4 py-2.5 font-mono opacity-70">{_fmtDuration(r.duration_ms)}</td>
                        <td className="px-4 py-2.5">
                          {r.ok
                            ? <span className="opacity-80">{_fmtResult(r.result) || 'ok'}</span>
                            : <span className="text-red-500 flex items-center gap-1.5"><WarningCircle className="size-3.5" />{r.error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </section>

            <section className="space-y-3" data-testid="scheduler-audit-section">
              <div className="flex items-center gap-2">
                <ClockCounterClockwise className="size-5 opacity-60" />
                <h2 className="text-lg font-bold tracking-tight">Config audit log</h2>
                <span className="text-[11px] opacity-60 font-mono ml-auto">
                  {auditEntries.length} entries · last 50 changes
                </span>
              </div>
              <Card className="overflow-hidden">
                <table className="w-full text-[13px]" data-testid="scheduler-audit-table">
                  <thead className="bg-muted/40 text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2.5">When</th>
                      <th className="text-left px-4 py-2.5">Job</th>
                      <th className="text-left px-4 py-2.5">Changed</th>
                      <th className="text-left px-4 py-2.5">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditEntries.length === 0 ? (
                      <tr><td colSpan={4} className="px-4 py-6 text-center opacity-60 text-[12px]" data-testid="scheduler-audit-empty">
                        No config changes recorded yet. Pause a job, change a schedule, or flip a cron — entries will appear here.
                      </td></tr>
                    ) : auditEntries.map((e, i) => (
                      <tr key={e.id || i} className="border-t border-border/40" data-testid={`scheduler-audit-row-${i}`}>
                        <td className="px-4 py-2.5 font-mono text-[12px] opacity-80 whitespace-nowrap">
                          {_fmtRelative(e.changed_at)}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-[12px]">
                          {JOB_META[e.job]?.label || e.job}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(e.changed_fields || {}).map(([k, v]) => (
                              <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted font-mono text-[11px]">
                                <span className="opacity-60">{k}:</span>
                                <span className="line-through opacity-50">{String(v.before)}</span>
                                <span className="opacity-40">→</span>
                                <span className="font-bold">{String(v.after)}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-[11px] opacity-70">
                          {e.changed_by || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </section>
          </>
        )}

        {preview && (
          <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setPreview(null)}
            data-testid="scheduler-preview-modal"
          >
            <Card
              className="max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="text-xs uppercase tracking-wider opacity-60 font-mono">Dry-run preview</div>
                  <h3 className="text-xl font-bold tracking-tight mt-0.5">
                    {JOB_META[preview.job]?.label || preview.job}
                  </h3>
                  <p className="text-[12px] opacity-70 mt-1">
                    No alerts created, no emails sent, no Slack pinged.
                    This is what the scheduler WOULD do — risk-free.
                  </p>
                </div>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setPreview(null)}>
                  <X className="size-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-[11px] uppercase tracking-wider opacity-60 font-mono mb-2">Result</div>
                  <pre className="bg-muted/50 p-3 rounded text-[12px] font-mono whitespace-pre-wrap break-words" data-testid="scheduler-preview-result">
                    {JSON.stringify(preview.result, null, 2)}
                  </pre>
                </div>

                {preview.captured_notifications?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider opacity-60 font-mono mb-2">
                      Would send · {preview.captured_notifications.length} notification{preview.captured_notifications.length === 1 ? '' : 's'}
                    </div>
                    <div className="space-y-3">
                      {preview.captured_notifications.map((n, i) => (
                        <Card key={i} className="p-3 border-primary/30" data-testid={`scheduler-preview-notification-${i}`}>
                          <div className="flex items-center gap-2 text-[11px] font-mono opacity-70 mb-2">
                            <span className="uppercase tracking-wider">{n.category}</span>
                            <span>·</span>
                            <span>{n.would_email?.length || 0} email{n.would_email?.length === 1 ? '' : 's'}</span>
                            <span>·</span>
                            <span>{n.would_slack ? 'slack ✓' : 'slack —'}</span>
                          </div>
                          <div className="font-bold text-[13px]">{n.subject}</div>
                          <pre className="text-[11px] opacity-80 whitespace-pre-wrap mt-2 font-mono">{n.body}</pre>
                          {n.would_email?.length > 0 && (
                            <div className="text-[10px] opacity-60 mt-2 font-mono">
                              To: {n.would_email.join(', ')}
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {!preview.ok && (
                  <div className="text-[12px] text-red-500 font-mono p-3 bg-red-500/10 rounded">
                    Error: {preview.error}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
